import { useEffect, useMemo, useRef, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Loader2, MessageSquare, RotateCcw, Send, Sparkles, Target, TrendingUp, Clock3 } from "lucide-react";
import { askPipeline } from "../lib/gemini";
import Markdown from "react-markdown";

const CHAT_STORAGE_KEY = "landminer.pipeline-chat.v1";
const MAX_STORED_MESSAGES = 24;
const MAX_CONTEXT_LEADS = 30;
const MAX_CONTEXT_OPPORTUNITIES = 20;

type ChatRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: string;
}

const starterPrompts = [
  "Which leads look most motivated right now?",
  "Rank my best opportunities and explain why.",
  "What follow-ups should I prioritize this week?",
  "Suggest next steps for the highest-value leads.",
];

function truncateText(value: string | null | undefined, maxLength: number) {
  if (!value) return "";
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
}

function formatMoney(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
}

function safeDateValue(value?: string) {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export default function ChatWithPipeline() {
  const { leads, opportunities } = useAppStore();
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(CHAT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        if (Array.isArray(parsed)) {
          setMessages(
            parsed
              .filter((message) => message && (message.role === "user" || message.role === "assistant") && typeof message.content === "string")
              .slice(-MAX_STORED_MESSAGES)
          );
        }
      }
    } catch (error) {
      console.warn("Failed to restore pipeline chat history", error);
    } finally {
      setIsHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isHydrated) return;
    try {
      window.localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)));
    } catch (error) {
      console.warn("Failed to persist pipeline chat history", error);
    }
  }, [isHydrated, messages]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isTyping]);

  const pipelineSnapshot = useMemo(() => {
    const leadStatusCounts = leads.reduce<Record<string, number>>((acc, lead) => {
      acc[lead.status] = (acc[lead.status] || 0) + 1;
      return acc;
    }, {});

    const openFollowUps = leads.reduce((count, lead) => {
      return count + (lead.followUps?.filter((followUp) => !followUp.completed).length || 0);
    }, 0);

    const priorityLeads = [...leads]
      .sort((left, right) => {
        const scoreDelta = (right.opportunityScore ?? 0) - (left.opportunityScore ?? 0);
        if (scoreDelta !== 0) return scoreDelta;
        return safeDateValue(right.updatedAt) - safeDateValue(left.updatedAt);
      })
      .slice(0, MAX_CONTEXT_LEADS)
      .map((lead) => ({
        name: lead.sellerName,
        address: lead.propertyAddress,
        status: lead.status,
        askingPrice: lead.askingPrice,
        opportunityScore: lead.opportunityScore ?? null,
        motivation: truncateText(lead.motivation, 160),
        followUpsOpen: lead.followUps?.filter((followUp) => !followUp.completed).length || 0,
        nextFollowUpDue: lead.followUps
          ?.filter((followUp) => !followUp.completed)
          .sort((left, right) => safeDateValue(left.dueDate) - safeDateValue(right.dueDate))[0]?.dueDate || null,
        updatedAt: lead.updatedAt,
      }));

    const priorityOpportunities = [...opportunities]
      .sort((left, right) => right.opportunityScore - left.opportunityScore)
      .slice(0, MAX_CONTEXT_OPPORTUNITIES)
      .map((opportunity) => ({
        address: opportunity.propertyAddress,
        score: opportunity.opportunityScore,
        tags: opportunity.tags.slice(0, 6),
        source: opportunity.source,
        notes: truncateText(opportunity.notes, 160),
        savedAt: opportunity.savedAt,
      }));

    const highPriorityCount =
      leads.filter((lead) => (lead.opportunityScore ?? 0) >= 80).length +
      opportunities.filter((opportunity) => opportunity.opportunityScore >= 80).length;

    return {
      counts: {
        leads: leads.length,
        opportunities: opportunities.length,
        openFollowUps,
        highPriority: highPriorityCount,
      },
      leadStatusCounts,
      priorityLeads,
      priorityOpportunities,
    };
  }, [leads, opportunities]);

  const contextPayload = useMemo(() => {
    return JSON.stringify(
      {
        workspace: {
          counts: pipelineSnapshot.counts,
          leadStatusCounts: pipelineSnapshot.leadStatusCounts,
          assistantScope: "Use only the provided workspace data. If a detail is missing, say so.",
        },
        leads: pipelineSnapshot.priorityLeads,
        opportunities: pipelineSnapshot.priorityOpportunities,
      },
      null,
      2
    );
  }, [pipelineSnapshot]);

  const stats = [
    { label: "Leads", value: pipelineSnapshot.counts.leads, icon: Target },
    { label: "Opportunities", value: pipelineSnapshot.counts.opportunities, icon: TrendingUp },
    { label: "Open follow-ups", value: pipelineSnapshot.counts.openFollowUps, icon: Clock3 },
    { label: "High priority", value: pipelineSnapshot.counts.highPriority, icon: Sparkles },
  ];

  const sendMessage = async (messageText: string) => {
    const trimmed = messageText.trim();
    if (!trimmed || isTyping) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: trimmed,
      createdAt: new Date().toISOString(),
    };

    setMessages((previous) => [...previous, userMessage]);
    setQuery("");
    setIsTyping(true);

    try {
      const response = await askPipeline(trimmed, contextPayload);
      setMessages((previous) => [
        ...previous,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: response,
          createdAt: new Date().toISOString(),
        },
      ]);
    } catch (error) {
      console.error(error);
      setMessages((previous) => [
        ...previous,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: "Sorry — I couldn’t query the pipeline just now.",
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleResetConversation = () => {
    setMessages([]);
    setQuery("");
    try {
      window.localStorage.removeItem(CHAT_STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear pipeline chat history", error);
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-8rem)] w-full max-w-5xl flex-col px-4">
      <Card className="flex h-full flex-col overflow-hidden border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 pb-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <CardTitle className="flex items-center gap-2 text-xl">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Chat with Pipeline
              </CardTitle>
              <p className="max-w-2xl text-sm text-slate-500">
                Ask questions about the current workspace, summarize priorities, or get follow-up recommendations without leaving your pipeline.
              </p>
              <div className="flex flex-wrap gap-2">
                {stats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <Badge key={stat.label} variant="outline" className="gap-1.5 rounded-full px-3 py-1 text-slate-600">
                      <Icon className="h-3.5 w-3.5" />
                      {stat.label}: {stat.value}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 self-start">
              <Button variant="outline" size="sm" onClick={handleResetConversation} disabled={messages.length === 0 && !query}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col gap-4 p-4 lg:p-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-900">Workspace snapshot</p>
                <p className="text-sm text-slate-600">
                  The assistant sees {pipelineSnapshot.counts.leads} leads and {pipelineSnapshot.counts.opportunities} opportunities, plus {pipelineSnapshot.counts.openFollowUps} open follow-ups.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                {Object.entries(pipelineSnapshot.leadStatusCounts).slice(0, 4).map(([status, count]) => (
                  <div key={status} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-600">
                    <div className="font-medium capitalize text-slate-900">{status}</div>
                    <div>{count} leads</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {starterPrompts.map((prompt) => (
                <Button
                  key={prompt}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void sendMessage(prompt);
                  }}
                  disabled={isTyping}
                  className="h-auto whitespace-normal rounded-full px-3 py-2 text-left"
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            <div className="space-y-4">
              {messages.length === 0 ? (
                <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50/40 text-center">
                  <div className="max-w-md space-y-3 px-6 py-12">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                      <MessageSquare className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-base font-medium text-slate-900">Start with a prompt chip or type a question.</p>
                      <p className="mt-1 text-sm leading-6 text-slate-500">
                        Try asking for motivation patterns, deal prioritization, or follow-up suggestions based on the active pipeline.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[90%] rounded-3xl px-4 py-3 shadow-sm sm:max-w-[80%] ${
                        message.role === "user"
                          ? "rounded-br-md bg-blue-600 text-white"
                          : "rounded-bl-md border border-slate-200 bg-white text-slate-800"
                      }`}
                    >
                      <div className={`text-[11px] font-medium uppercase tracking-wide ${message.role === "user" ? "text-blue-100" : "text-slate-400"}`}>
                        {message.role === "user" ? "You" : "Assistant"}
                      </div>
                      <div className={`mt-2 whitespace-pre-wrap break-words text-sm leading-7 ${message.role === "user" ? "text-white" : "text-slate-700"}`}>
                        {message.role === "assistant" ? (
                          <div className="prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1 prose-headings:mb-2 prose-headings:mt-4 first:prose-headings:mt-0 [&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:bg-slate-950 [&_pre]:p-3 [&_pre]:text-slate-100">
                            <Markdown>{message.content}</Markdown>
                          </div>
                        ) : (
                          message.content
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-2 rounded-3xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                    Thinking through the pipeline…
                  </div>
                </div>
              )}
              <div ref={scrollRef} />
            </div>
          </div>
        </CardContent>

        <CardFooter className="border-t border-slate-100 p-4 lg:p-6">
          <div className="flex w-full flex-col gap-3">
            <Textarea
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void sendMessage(query);
                }
              }}
              placeholder="Search leads, prioritize deals, or ask for follow-up recommendations..."
              className="min-h-24 resize-none"
              aria-label="Ask the pipeline assistant"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">Press Enter to send. Shift+Enter adds a new line.</p>
              <Button onClick={() => void sendMessage(query)} disabled={isTyping || !query.trim()}>
                <Send className="mr-2 h-4 w-4" />
                Send
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
